import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import StatusBadge from "../../components/common/StatusBadge";
import { useToast } from "../../contexts/ToastContext";
import { authService } from "../../services/auth";
import { organizationsService } from "../../services/organizations";
import { getApiErrorMessage } from "../../utils/axios";

const PLAN_LABELS = {
  blueprint: "Blueprint",
  launch: "Launch",
  builder: "Builder",
  factory: "Factory",
  enterprise: "Enterprise",
};

const UsageBar = ({ label, used, allowed, periodLabel }) => {
  const unlimited = allowed == null;
  const ratio = unlimited || !allowed ? 0 : used / allowed;
  const pct = unlimited ? Math.min(100, used ? 8 : 0) : Math.min(100, ratio * 100);
  // over = red, at limit = yellow, below = green
  let barClass = "bg-emerald-500";
  if (!unlimited) {
    if (used > allowed) barClass = "bg-red-500";
    else if (used >= allowed) barClass = "bg-amber-400";
  }
  const countLabel = unlimited
    ? `${used} used (unlimited)`
    : `${used}/${allowed}${periodLabel ? ` ${periodLabel}` : ""}`;

  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="opacity-60">{countLabel}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-theme-secondary">
        <div
          className={`h-full transition-all ${barClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const Account = ({ currentUser, setCurrentUser }) => {
  const { error: toastError, success: toastSuccess } = useToast();
  const limits = currentUser?.limits || {};
  const planKey = limits.key || "blueprint";
  const sitesUsed = limits.sites_used ?? 0;
  const sitesAllowed = limits.sites_allowed ?? 1;
  const changesUsed = limits.changes_used ?? 0;
  const changesAllowed = limits.changes_allowed;
  const changesPeriod = limits.changes_period;
  const periodLabel = changesPeriod === "monthly" ? "/mo" : "";

  const [organization, setOrganization] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [savingOrg, setSavingOrg] = useState(false);

  const canEditOrg =
    organization?.role === "owner" || organization?.role === "admin";
  const orgNameDirty =
    orgName.trim() !== "" && orgName.trim() !== (organization?.name || "");

  useEffect(() => {
    organizationsService.list(
      (res) => {
        const rows = res.data?.results || res.data || [];
        const org = rows[0] || null;
        setOrganization(org);
        setOrgName(org?.name || "");
      },
      () => toastError("Could not load organization"),
    );
  }, [toastError]);

  const saveOrganization = () => {
    if (!organization || !canEditOrg || !orgNameDirty || savingOrg) return;
    setSavingOrg(true);
    organizationsService.update(
      organization.hash,
      { name: orgName.trim() },
      (res) => {
        setOrganization(res.data);
        setOrgName(res.data.name);
        setSavingOrg(false);
        authService.fetchUser((userRes) => {
          if (setCurrentUser) setCurrentUser(userRes.data);
        });
        toastSuccess("Organization updated");
      },
      (err) => {
        setSavingOrg(false);
        toastError(getApiErrorMessage(err, "Could not update organization"));
      },
    );
  };

  return (
    <div className="max-w-2xl space-y-8">
      <section>
        <h2 className="mb-4 text-sm font-semibold">Profile</h2>
        <label className="block text-xs font-medium">
          Email address
          <input
            value={currentUser?.identity?.email || ""}
            disabled
            className="mt-2 block w-full rounded-lg border border-theme-secondary px-4 py-3 text-sm disabled:opacity-60"
          />
        </label>
        <label className="mt-4 block text-xs font-medium">
          Organization
          <div className="mt-2 flex items-center gap-2">
            <input
              value={orgName}
              disabled={!canEditOrg || savingOrg}
              onChange={(e) => setOrgName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveOrganization();
                }
              }}
              className="min-w-0 flex-1 rounded-lg border border-theme-secondary px-4 py-3 text-sm disabled:opacity-60"
            />
            {canEditOrg && (
              <button
                type="button"
                disabled={!orgNameDirty || savingOrg}
                onClick={saveOrganization}
                className="shrink-0 rounded-lg bg-theme-primary px-5 py-3 text-sm font-semibold text-theme-button disabled:opacity-50"
              >
                Save
              </button>
            )}
          </div>
        </label>
      </section>

      <section>
        <h2 className="mb-4 text-sm font-semibold">Usage</h2>
        <div className="rounded-xl border border-theme-secondary p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Current plan</h3>
            <StatusBadge variant="neutral" label={PLAN_LABELS[planKey] || planKey} />
          </div>
          <div className="mt-5 space-y-4">
            <UsageBar label="Sites" used={sitesUsed} allowed={sitesAllowed} />
            <UsageBar
              label="Change requests"
              used={changesUsed}
              allowed={changesAllowed}
              periodLabel={periodLabel}
            />
          </div>
          <Link
            to="/account/billing/"
            className="mt-5 inline-block rounded-lg bg-theme-primary px-5 py-2 text-sm font-semibold text-theme-button"
          >
            Upgrade to increase limits
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Account;
